/** Booking checks consume the verified schedule attached by the server reader.
 * A failed ownership read must never revive the compatibility JSON in a basket.
 */
export const SCHEDULE_BOOKING_UNAVAILABLE =
  "These course dates could not be verified. Refresh the page or contact Student Support before booking.";

export function courseScheduleBookingError(event: any): string | null {
  const result = event?.courseSchedule;
  if (!result?.ok || result.schedule?.bookingAllowed !== true) {
    return SCHEDULE_BOOKING_UNAVAILABLE;
  }
  return null;
}

export function basketScheduleBookingError(courses: any[]): string | null {
  for (const course of courses || []) {
    // Online, RPL and explicitly undated purchases have no selected event.
    if (!course?.selectedEvent) continue;
    const error = courseScheduleBookingError(course.selectedEvent);
    if (error) return error;
  }
  return null;
}

/** Model capacity and pricing belong to the stored event_session, not the
 * student course's sellable flag or its number of student places.
 */
export function modelScheduleBookingError(session: any): string | null {
  const parent = session?.event;
  const result = parent?.courseSchedule;
  if (!parent?.id || !result?.ok) return SCHEDULE_BOOKING_UNAVAILABLE;
  if (parent.published === false) return "This model session is not available for booking.";
  if (parent.published !== true) return "This model session is not available for booking.";
  if (["ARCHIVED", "CANCELLED", "CANCELED"].includes(String(parent.status || "").toUpperCase())) {
    return "This model session is no longer available.";
  }
  if (result.schedule?.mode === "CANONICAL_V2" && (
    !result.schedule.delivery?.some((day: any) => day.state === "CONFIRMED")
  )) return "This model session is not available for booking.";
  return null;
}

export function scheduleRevisionMatches(snapshot: any, current: any): boolean {
  if (current?.courseSchedule?.schedule?.mode !== "CANONICAL_V2") return true;
  const previous = snapshot?.courseSchedule;
  return previous?.ok === true && previous.schedule?.mode === "CANONICAL_V2" &&
    String(previous.schedule.revision) === String(current.courseSchedule.schedule.revision);
}

/** Zero is a valid stored net price. Never use a catalogue fallback for it. */
export function storedCoursePricePence(event: any): number | null {
  const raw = event?.seat_price ?? event?.price;
  if (raw === null || raw === undefined || raw === "") return null;
  const price = Number(raw);
  return Number.isSafeInteger(price) && price >= 0 ? price : null;
}

export function modelSessionSnapshotMatches(snapshot: any, current: any): boolean {
  const sameInstant = (a: unknown, b: unknown) => typeof a === "string" && typeof b === "string" &&
    Number.isFinite(Date.parse(a)) && Date.parse(a) === Date.parse(b);
  return String(snapshot?.id) === String(current?.id) &&
    String(snapshot?.event_id ?? snapshot?.event?.id) === String(current?.event?.id) &&
    sameInstant(snapshot?.start, current?.start) && sameInstant(snapshot?.end, current?.end) &&
    storedCoursePricePence(snapshot) !== null &&
    storedCoursePricePence(snapshot) === storedCoursePricePence(current);
}
